/* eslint-disable camelcase */
import React, { forwardRef, useImperativeHandle, useEffect } from "react"
import {
  Grid,
  TextField,
  Box,
  Paper,
  Typography,
  MenuItem,
  InputAdornment,
  FormControlLabel,
  Switch,
  Autocomplete,
} from "@mui/material"
import { Badge, AssignmentInd, Phone } from "@mui/icons-material"
import { useFormik } from "formik"
import * as Yup from "yup"
import { useQuery } from "@tanstack/react-query"
import { api } from "../../../../api"

const FormularioDelegadoCordinador = forwardRef(({ onSubmit, isLoading, modo, initialData }, ref) => {
  // ========================================================
  // 1. LÓGICA DEFENSIVA PARA USUARIOS (Misma que Balanzas)
  // ========================================================
  const { data: rawUsuarios, isLoading: isLoadingUsr } = useQuery({
    queryKey: ["listaUsuariosBalanzas"],
    queryFn: async () => {
      try {
        const response = await api.get("/Agenda/getAllUsuariosCB")
        return response?.data?.data || response?.data || response || []
      } catch (error) {
        console.error("Error al cargar Usuarios:", error)
        return []
      }
    },
  })

  const usrValidados = Array.isArray(rawUsuarios)
    ? rawUsuarios
    : Array.isArray(rawUsuarios?.data)
      ? rawUsuarios.data
      : []

  const listaUsuariosBase = usrValidados.map((item) => ({
    id: item.value || item.usrcodigo || item.id,
    label: item.label || `${item.value || item.usrcodigo} - ${item.usrnombre || ""}`,
  }))

  const listaUsuarios = [{ id: "", label: "Ninguno (Sin acceso a sistema)" }, ...listaUsuariosBase]

  // ========================================================
  // 2. LÓGICA DEFENSIVA PARA RECINTOS (Misma que Balanzas)
  // ========================================================
  const { data: rawRecintos, isLoading: isLoadingRec } = useQuery({
    queryKey: ["listaRecintosBalanzas"],
    queryFn: async () => {
      try {
        const response = await api.post("/ControlElectoral/getAllRecintos", {
          page: 1,
          perPage: 10000,
          filters: {}, // Diccionario requerido por el backend
        })
        return response?.data?.data || response?.data || response || []
      } catch (error) {
        console.error("Error al cargar Recintos:", error)
        return []
      }
    },
  })

  const recValidados = Array.isArray(rawRecintos)
    ? rawRecintos
    : Array.isArray(rawRecintos?.data)
      ? rawRecintos.data
      : []

  const listaRecintos = recValidados.map((item) => ({
    id: item.reci_codigo || item.id,
    label: item.label || `${item.reci_codigo} - ${item.reci_nombre || ""}`,
  }))

  // ========================================================
  // 3. CONFIGURACIÓN DE VALIDACIONES (YUP) Y ESTADO (FORMIK)
  // ========================================================
  const formik = useFormik({
    initialValues: {
      del_codigo: "",
      usrcodigo: "",
      reci_codigo: "",
      del_nombre: "",
      del_telefono: "",
      del_rol: "DELEGADO",
      del_asistencia: 0,
    },
    validationSchema: Yup.object({
      del_codigo: Yup.string().required("La cédula/identificación es obligatoria").max(15, "Máximo 15 caracteres"),
      usrcodigo: Yup.string().nullable(),
      reci_codigo: Yup.string().required("El recinto asignado es obligatorio"),
      del_nombre: Yup.string().required("El nombre completo es obligatorio").max(150, "Máximo 150 caracteres"),
      del_telefono: Yup.string().max(15, "Máximo 15 caracteres").nullable(),
      del_rol: Yup.string()
        .required("El rol es obligatorio")
        .oneOf(["DELEGADO", "COORDINADOR"], "Seleccione un rol válido"),
    }),
    onSubmit: async (values) => {
      if (modo === "editar") {
        const payloadEdit = {
          del_codigoOld: initialData.del_codigo,
          usrcodigoNew: values.usrcodigo,
          reci_codigoNew: values.reci_codigo,
          del_nombreNew: values.del_nombre,
          del_telefonoNew: values.del_telefono,
          del_rolNew: values.del_rol,
          del_asistenciaNew: values.del_asistencia ? 1 : 0,
        }
        await onSubmit(payloadEdit)
      } else {
        const payloadCreate = {
          ...values,
          del_asistencia: values.del_asistencia ? 1 : 0,
        }
        await onSubmit(payloadCreate)
      }
    },
  })

  useImperativeHandle(ref, () => ({
    handleSubmit: formik.submitForm,
  }))

  useEffect(() => {
    if (modo === "editar" && initialData) {
      const asistenciaBooleana =
        initialData.del_asistencia === 1 || initialData.del_asistencia === true || initialData.del_asistencia === "1"

      formik.setValues({
        del_codigo: initialData.del_codigo || "",
        usrcodigo: initialData.usrcodigo ? String(initialData.usrcodigo) : "",
        reci_codigo: initialData.reci_codigo ? String(initialData.reci_codigo) : "",
        del_nombre: initialData.del_nombre || "",
        del_telefono: initialData.del_telefono || "",
        del_rol: initialData.del_rol || "DELEGADO",
        del_asistencia: asistenciaBooleana,
      })
    }
  }, [modo, initialData])

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={3}>
        {/* SECCIÓN 1: DATOS PERSONALES */}
        <Grid item xs={12} md={7}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: "100%" }}>
            <Typography variant="h6" color="primary" fontWeight="bold" mb={3} display="flex" alignItems="center">
              <Badge sx={{ mr: 1 }} /> Datos del Personal
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  id="del_codigo"
                  name="del_codigo"
                  label="Cédula / ID *"
                  disabled={modo === "editar" || isLoading}
                  value={formik.values.del_codigo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.del_codigo && Boolean(formik.errors.del_codigo)}
                  helperText={formik.touched.del_codigo && formik.errors.del_codigo}
                  inputProps={{ maxLength: 15, style: { fontWeight: "bold" } }}
                />
              </Grid>

              <Grid item xs={12} sm={7}>
                <TextField
                  fullWidth
                  id="del_telefono"
                  name="del_telefono"
                  label="Número Telefónico"
                  disabled={isLoading}
                  value={formik.values.del_telefono}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.del_telefono && Boolean(formik.errors.del_telefono)}
                  helperText={formik.touched.del_telefono && formik.errors.del_telefono}
                  inputProps={{ maxLength: 15 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="del_nombre"
                  name="del_nombre"
                  label="Nombre Completo *"
                  disabled={isLoading}
                  value={formik.values.del_nombre}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.del_nombre && Boolean(formik.errors.del_nombre)}
                  helperText={formik.touched.del_nombre && formik.errors.del_nombre}
                  inputProps={{ maxLength: 150 }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* SECCIÓN 2: ASIGNACIÓN LOGÍSTICA Y SEGURIDAD */}
        <Grid item xs={12} md={5}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: "100%", bgcolor: "#f9fbfd" }}>
            <Typography variant="h6" color="primary" fontWeight="bold" mb={3} display="flex" alignItems="center">
              <AssignmentInd sx={{ mr: 1 }} /> Asignación y Sistema
            </Typography>

            <Box mb={2}>
              <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                Vincule la cuenta de acceso del sistema (SIAC) para aplicar la seguridad a nivel de fila y defina su rol
                logístico.
              </Typography>

              <Grid container spacing={2}>
                {/* COMBO DE USUARIOS (Igual a Balanzas) */}
                <Grid item xs={12}>
                  <Autocomplete
                    options={listaUsuarios}
                    getOptionLabel={(option) => option.label || ""}
                    value={
                      listaUsuarios.find((c) => c.id === formik.values.usrcodigo) ||
                      (formik.values.usrcodigo
                        ? { id: formik.values.usrcodigo, label: `${formik.values.usrcodigo}` }
                        : null)
                    }
                    onChange={(event, newValue) => {
                      formik.setFieldValue("usrcodigo", newValue ? newValue.id : "")
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    disabled={isLoading || isLoadingUsr}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Usuario de Sistema SIAC (Opcional)"
                        placeholder={isLoadingUsr ? "Cargando..." : "Buscar usuario..."}
                        InputLabelProps={{ shrink: true }}
                        error={formik.touched.usrcodigo && Boolean(formik.errors.usrcodigo)}
                        helperText={(formik.touched.usrcodigo && formik.errors.usrcodigo) || "Seleccione el usuario"}
                      />
                    )}
                  />
                </Grid>

                {/* COMBO DE RECINTOS (Igual a Balanzas) */}
                <Grid item xs={12}>
                  <Autocomplete
                    options={listaRecintos}
                    getOptionLabel={(option) => option.label || ""}
                    value={
                      listaRecintos.find((c) => c.id === formik.values.reci_codigo) ||
                      (formik.values.reci_codigo
                        ? { id: formik.values.reci_codigo, label: `${formik.values.reci_codigo}` }
                        : null)
                    }
                    onChange={(event, newValue) => {
                      formik.setFieldValue("reci_codigo", newValue ? newValue.id : "")
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    disabled={isLoading || isLoadingRec}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Recinto Asignado *"
                        placeholder={isLoadingRec ? "Cargando..." : "Buscar recinto..."}
                        InputLabelProps={{ shrink: true }}
                        error={formik.touched.reci_codigo && Boolean(formik.errors.reci_codigo)}
                        helperText={
                          (formik.touched.reci_codigo && formik.errors.reci_codigo) || "Seleccione el recinto"
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    id="del_rol"
                    name="del_rol"
                    label="Rol Logístico *"
                    disabled={isLoading}
                    value={formik.values.del_rol}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.del_rol && Boolean(formik.errors.del_rol)}
                    helperText={formik.touched.del_rol && formik.errors.del_rol}
                  >
                    <MenuItem value="DELEGADO">DELEGADO (Mesa)</MenuItem>
                    <MenuItem value="COORDINADOR">COORDINADOR (Recinto)</MenuItem>
                  </TextField>
                </Grid>

                {modo === "editar" && (
                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 1.5, bgcolor: "white" }}>
                      <FormControlLabel
                        control={
                          <Switch
                            id="del_asistencia"
                            name="del_asistencia"
                            checked={formik.values.del_asistencia}
                            onChange={formik.handleChange}
                            disabled={isLoading}
                            color="success"
                          />
                        }
                        label={
                          <Typography fontWeight="bold" color={formik.values.del_asistencia ? "green" : "error"}>
                            {formik.values.del_asistencia ? "ASISTENCIA REGISTRADA" : "ASISTENCIA PENDIENTE"}
                          </Typography>
                        }
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </form>
  )
})

export default FormularioDelegadoCordinador
